/**
 * Email renderer for designed emails (blocks or sanitized HTML) → client-safe HTML + plain text.
 *
 *  - table layout, inline styles, 600px card that becomes fluid on phones, `<style>` only for
 *    progressive enhancement (mobile stacking, dark mode in clients that support it)
 *  - preheader, alt texts, `lang`, `color-scheme` metas, bulletproof buttons
 *  - merge variables with the composer's fallbacks (`{{firstName|φίλε μας}}`); merged values are
 *    always HTML-escaped and never parsed as formatting or links
 *  - the unsubscribe footer is ALWAYS appended (it is not a block and cannot be removed by a
 *    template); the provider additionally gets List-Unsubscribe headers
 *  - `linkUrl` maps http(s) links to tracked redirects at send time
 *
 * Pure and dependency-free: the API renders what is sent, the web editor renders its live preview
 * with the same code. HTML-mode input MUST already be sanitized by the API (`sanitizeEmailHtml`).
 */
import {
  MERGE_VARIABLES,
  escapeHtml,
  renderMergeTemplate,
  type MergeValues,
  type MergeVariable,
  type RenderResult,
} from './contracts/messaging';
import {
  EMAIL_FONT_STACKS,
  EMAIL_PALETTE,
  FORM_LINK_HREF,
  ensureContrast,
  readableOn,
  tint,
  type EmailBlock,
  type EmailBlocksDesign,
  type EmailCell,
  type EmailDesign,
  type EmailTheme,
  type SocialNetwork,
} from './emailDesign';
import { quoteName } from './greek';

export interface EmailRenderOptions {
  subject: string;
  businessName: string;
  locale: 'el' | 'en';
  /** Public URL of the business logo (logo blocks without their own image). */
  logoUrl?: string | null;
  /** Merge values (firstName already in the vocative where applicable). */
  values: MergeValues;
  /** The recipient's unsubscribe page — always rendered in the footer. */
  unsubscribeUrl: string;
  /** Tracked-link mapping for http(s) links (send time). */
  linkUrl?: (url: string) => string;
  /** Preview only: force the dark palette regardless of the viewer's settings. */
  forceDark?: boolean;
  /** Plain text only (send plans/estimates): skips building the HTML (`html` is ''). */
  textOnly?: boolean;
}

export interface RenderedEmail {
  html: string;
  /** Plain-text alternative, footer included. */
  text: string;
  missing: string[];
  unknown: string[];
}

/** Separator between the message text and the unsubscribe footer (shared with render.ts). */
export const EMAIL_FOOTER_SEPARATOR = '\n\n—\n';

/** The legally required footer as plain text. */
export function emailFooterText(locale: 'el' | 'en', businessName: string, url: string): string {
  return locale === 'en'
    ? `You are receiving this email as a customer of ${businessName}. To stop receiving emails: ${url}`
    : `Λαμβάνετε αυτό το email ως πελάτης του ${quoteName(businessName)}. Για να μη λαμβάνετε άλλα email: ${url}`;
}

function footerHtml(locale: 'el' | 'en', businessName: string, url: string, color: string) {
  const link = `<a href="${escapeHtml(url)}" style="color:${color};text-decoration:underline;" class="gh-muted">${
    locale === 'en' ? 'Unsubscribe' : 'Διαγραφή από τη λίστα'
  }</a>`;
  const lead =
    locale === 'en'
      ? `You are receiving this email as a customer of ${escapeHtml(businessName)}.`
      : `Λαμβάνετε αυτό το email ως πελάτης του ${escapeHtml(quoteName(businessName))}.`;
  return `${lead} ${link}`;
}

// ─── merge with sentinels (values are escaped, never formatted) ─────────────

const S_OPEN = '\ue000';
const S_CLOSE = '\ue001';
const SENTINEL_RE = /\ue000(\d+)\ue001/g;

interface Merged {
  text: string;
  subs: string[];
  r: RenderResult;
}

function mergeSentinels(source: string, values: MergeValues): Merged {
  const subs: string[] = [];
  const proxied: MergeValues = {};
  for (const [k, v] of Object.entries(values) as [keyof MergeValues, string | null | undefined][]) {
    const s = v == null ? '' : String(v).trim();
    if (!s) continue;
    subs.push(s);
    proxied[k] = `${S_OPEN}${subs.length - 1}${S_CLOSE}`;
  }
  const r = renderMergeTemplate(source, proxied);
  return { text: r.text, subs, r };
}

/**
 * A link URL that contains merge values: they are URL-encoded and only allowed after the host
 * (`https://shop.gr/?ref={{firstName}}`), never in the scheme or host — a recipient's name must
 * not be able to change where a link goes. Returns '' (no link) otherwise.
 */
function urlWithValues(url: string, subs: string[]): string {
  if (!url.includes(S_OPEN)) return url;
  const first = url.indexOf(S_OPEN);
  if (!/^https?:\/\/[^/?#\s]+[/?#]/i.test(url.slice(0, first))) return '';
  return url.replace(SENTINEL_RE, (_m, i: string) => encodeURIComponent(subs[Number(i)] ?? ''));
}

function unsentinel(s: string, subs: string[], escape: boolean): string {
  return s.replace(SENTINEL_RE, (_m, i: string) => {
    const v = subs[Number(i)] ?? '';
    return escape ? escapeHtml(v) : v;
  });
}

// ─── inline formatting ─────────────────────────────────────────────────────────

function safeHttp(url: string): string | null {
  const u = url.replace(/&amp;/g, '&');
  return /^https?:\/\/[^\s<>"']+$/i.test(u) ||
    /^mailto:[^\s<>"']+$/i.test(u) ||
    /^tel:[+0-9()\-. ]{3,30}$/i.test(u)
    ? u
    : null;
}

interface Ctx {
  opts: EmailRenderOptions;
  theme: EmailTheme;
  missing: Set<string>;
  unknown: Set<string>;
  dark: { text: string; muted: string };
}

/**
 * A block link target: `{{formLink}}` (the only token a link may hold) becomes the server-built
 * personal form URL from the merge values ('' when there is none, e.g. outside a linked campaign).
 */
function linkTarget(ctx: Ctx, url: string): string {
  if (url !== FORM_LINK_HREF) return url;
  const v = ctx.opts.values.formLink;
  const s = v == null ? '' : String(v).trim();
  return safeHttp(s) ?? '';
}

function href(ctx: Ctx, url: string): string {
  if (url === FORM_LINK_HREF) return escapeHtml(linkTarget(ctx, url));
  const mapped = /^https?:/i.test(url) && ctx.opts.linkUrl ? ctx.opts.linkUrl(url) : url;
  return escapeHtml(mapped);
}

/** Escaped HTML of an author text: merge → escape → **bold**, _italic_, [label](url), bare links, line breaks. */
function richHtml(ctx: Ctx, text: string, linkColor: string): string {
  const m = mergeSentinels(text.replace(/\r\n/g, '\n'), ctx.opts.values);
  m.r.missing.forEach((v) => ctx.missing.add(v));
  m.r.unknown.forEach((v) => ctx.unknown.add(v));
  const linkStyle = `color:${linkColor};text-decoration:underline;`;
  let s = escapeHtml(m.text);
  s = s.replace(/\[([^\]]{1,200})\]\(([^)\s]+)\)/g, (_all, label: string, url: string) => {
    const u = safeHttp(urlWithValues(url, m.subs));
    return u ? `<a href="${href(ctx, u)}" style="${linkStyle}">${label}</a>` : label;
  });
  s = s.replace(
    /(^|[\s(])(https?:\/\/[^\s<\ue000]+[^\s<.,;:!?)\ue000\ue001])/g,
    (all, pre: string, url: string) => {
      const u = safeHttp(url);
      return u ? `${pre}<a href="${href(ctx, u)}" style="${linkStyle}">${url}</a>` : all;
    },
  );
  s = s.replace(/\*\*([^*\n]{1,500})\*\*/g, '<strong>$1</strong>');
  s = s.replace(/(^|[\s(>])_([^_\n]{1,500})_(?=[\s).,;:!?<]|$)/g, '$1<em>$2</em>');
  s = s.replace(/\n/g, '<br>');
  return unsentinel(s, m.subs, true);
}

/** Plain text of an author text (markers removed, links as "label (url)"). */
function richText(ctx: Ctx, text: string): string {
  const m = mergeSentinels(text.replace(/\r\n/g, '\n'), ctx.opts.values);
  m.r.missing.forEach((v) => ctx.missing.add(v));
  m.r.unknown.forEach((v) => ctx.unknown.add(v));
  const out = m.text
    .replace(/\[([^\]]{1,200})\]\(([^)\s]+)\)/g, (_a, l: string, u: string) => {
      const url = safeHttp(urlWithValues(u, m.subs));
      return url ? `${l} (${url})` : l;
    })
    .replace(/\*\*([^*\n]+)\*\*/g, '$1')
    .replace(/(^|[\s(])_([^_\n]+)_(?=[\s).,;:!?]|$)/g, '$1$2');
  return unsentinel(out, m.subs, false).trim();
}

function plainHtml(ctx: Ctx, text: string): string {
  const m = mergeSentinels(text, ctx.opts.values);
  m.r.missing.forEach((v) => ctx.missing.add(v));
  m.r.unknown.forEach((v) => ctx.unknown.add(v));
  return unsentinel(escapeHtml(m.text), m.subs, true);
}

function plainText(ctx: Ctx, text: string): string {
  const m = mergeSentinels(text, ctx.opts.values);
  m.r.missing.forEach((v) => ctx.missing.add(v));
  m.r.unknown.forEach((v) => ctx.unknown.add(v));
  return unsentinel(m.text, m.subs, false).trim();
}

// ─── blocks → HTML ─────────────────────────────────────────────────────────────

const SOCIAL_LABEL: Record<SocialNetwork, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  x: 'X',
  linkedin: 'LinkedIn',
  google: 'Google',
  website: 'Website',
};
const SOCIAL_LABEL_EL: Partial<Record<SocialNetwork, string>> = { website: 'Ιστοσελίδα' };

function socialLabel(ctx: Ctx, n: SocialNetwork) {
  return (ctx.opts.locale === 'el' ? SOCIAL_LABEL_EL[n] : undefined) ?? SOCIAL_LABEL[n];
}

const HEADING_SIZE = { 1: 30, 2: 23, 3: 18 } as const;
const PARA_SIZE = { sm: 14, md: 16, lg: 18 } as const;

function row(inner: string, pad = '0 40px', cls = 'gh-px') {
  return `<tr><td class="${cls}" style="padding:${pad};">${inner}</td></tr>`;
}

function cellHtml(ctx: Ctx, b: EmailCell | EmailBlock, inColumn: boolean, contentWidth: number) {
  const t = ctx.theme;
  const body = EMAIL_FONT_STACKS[t.font];
  const head = EMAIL_FONT_STACKS[t.headingFont];
  switch (b.type) {
    case 'heading': {
      const size = inColumn ? Math.min(HEADING_SIZE[b.level], 20) : HEADING_SIZE[b.level];
      const color = b.color ? ensureContrast(b.color, t.contentColor, 3) : t.textColor;
      return `<h${b.level} class="gh-text" style="margin:0;padding:${inColumn ? '0 0 8px' : '8px 0'};font-family:${head};font-size:${size}px;line-height:${b.level === 1 ? 1.15 : 1.25};font-weight:${b.level === 1 ? 800 : 700};color:${color};text-align:${b.align};letter-spacing:${b.level === 1 ? '-0.02em' : '-0.01em'};">${plainHtml(ctx, b.text)}</h${b.level}>`;
    }
    case 'paragraph': {
      const color = b.color ? ensureContrast(b.color, t.contentColor, 4.5) : t.textColor;
      return `<p class="gh-text" style="margin:0;padding:${inColumn ? '0 0 10px' : '6px 0'};font-family:${body};font-size:${PARA_SIZE[b.size]}px;line-height:1.6;color:${color};text-align:${b.align};">${richHtml(ctx, b.text, ensureContrast(t.brandColor, t.contentColor, 4.5))}</p>`;
    }
    case 'image': {
      if (!b.src) return '';
      const max = Math.round((contentWidth * b.width) / 100);
      const img = `<img src="${escapeHtml(b.src)}" alt="${plainHtml(ctx, b.alt)}" width="${max}" style="display:block;width:100%;max-width:${max}px;height:auto;border:0;outline:none;text-decoration:none;${b.rounded ? `border-radius:${Math.max(4, t.radius - 2)}px;` : ''}">`;
      const linked = linkTarget(ctx, b.href)
        ? `<a href="${href(ctx, b.href)}" style="text-decoration:none;">${img}</a>`
        : img;
      return `<table role="presentation" cellpadding="0" cellspacing="0" border="0" align="${b.align}" style="${b.align === 'center' ? 'margin:0 auto;' : ''}width:${b.width}%;max-width:${max}px;"><tr><td style="padding:${inColumn ? '0 0 10px' : '8px 0'};">${linked}</td></tr></table>`;
    }
    case 'button': {
      const bg = b.color ?? t.brandColor;
      const solid = b.variant === 'solid';
      const fg = solid ? readableOn(bg) : ensureContrast(bg, t.contentColor, 3);
      const radius = Math.min(t.radius, 10);
      const label = plainHtml(ctx, b.label || '…');
      const link = linkTarget(ctx, b.url)
        ? `<a href="${href(ctx, b.url)}" target="_blank" class="${b.fullWidth ? 'gh-btn-full' : ''}" style="display:${b.fullWidth ? 'block' : 'inline-block'};padding:14px 28px;font-family:${body};font-size:16px;line-height:1.2;font-weight:700;color:${fg};text-decoration:none;border-radius:${radius}px;">${label}</a>`
        : `<span style="display:inline-block;padding:14px 28px;font-family:${body};font-size:16px;line-height:1.2;font-weight:700;color:${fg};">${label}</span>`;
      const cellStyle = solid
        ? `background:${bg};border-radius:${radius}px;`
        : `border:2px solid ${bg};border-radius:${radius}px;`;
      return `<table role="presentation" cellpadding="0" cellspacing="0" border="0" align="${b.fullWidth ? 'center' : b.align}" style="${b.fullWidth ? 'width:100%;' : ''}${b.align === 'center' || b.fullWidth ? 'margin:0 auto;' : ''}"><tr><td style="padding:${inColumn ? '4px 0 10px' : '12px 0'};"><table role="presentation" cellpadding="0" cellspacing="0" border="0" style="${b.fullWidth ? 'width:100%;' : ''}"><tr><td align="center" ${solid ? `bgcolor="${bg}"` : ''} style="${cellStyle}">${link}</td></tr></table></td></tr></table>`;
    }
    default:
      return '';
  }
}

function blockRow(ctx: Ctx, b: EmailBlock, contentWidth: number): string {
  const t = ctx.theme;
  const body = EMAIL_FONT_STACKS[t.font];
  const head = EMAIL_FONT_STACKS[t.headingFont];
  switch (b.type) {
    case 'logo': {
      const src = b.src || ctx.opts.logoUrl || null;
      const inner = src
        ? `<img src="${escapeHtml(src)}" alt="${escapeHtml(b.alt || ctx.opts.businessName)}" width="${b.width}" style="display:inline-block;width:${b.width}px;max-width:100%;height:auto;border:0;outline:none;">`
        : `<span style="font-family:${head};font-size:21px;line-height:1.2;font-weight:800;letter-spacing:-0.01em;color:${ensureContrast(t.brandColor, t.contentColor, 3)};" class="gh-brand">${escapeHtml(ctx.opts.businessName)}</span>`;
      return row(`<div style="text-align:${b.align};">${inner}</div>`, '28px 40px 12px');
    }
    case 'heading':
    case 'paragraph':
    case 'image':
    case 'button':
      return row(cellHtml(ctx, b, false, contentWidth));
    case 'divider': {
      const color = b.color ?? tint(t.textColor, 0.82);
      return row(
        `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td class="gh-border" style="border-top:${b.thickness}px solid ${color};font-size:0;line-height:0;height:1px;">&nbsp;</td></tr></table>`,
        '16px 40px',
      );
    }
    case 'spacer':
      return `<tr><td style="height:${b.height}px;font-size:0;line-height:0;">&nbsp;</td></tr>`;
    case 'columns': {
      const [l, r] = b.ratio === '40-60' ? [40, 60] : b.ratio === '60-40' ? [60, 40] : [50, 50];
      const inner = contentWidth - 80;
      const col = (cells: EmailCell[], pct: number, side: 'l' | 'r') =>
        `<td class="gh-col" width="${pct}%" valign="top" style="width:${pct}%;padding:${side === 'l' ? '8px 12px 8px 0' : '8px 0 8px 12px'};vertical-align:top;">${cells
          .map((c) => cellHtml(ctx, c, true, Math.round((inner * pct) / 100)))
          .join('')}</td>`;
      return row(
        `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr>${col(b.left, l, 'l')}${col(b.right, r, 'r')}</tr></table>`,
      );
    }
    case 'callout': {
      const bg =
        b.tone === 'brand'
          ? t.brandColor
          : b.tone === 'accent'
            ? EMAIL_PALETTE.saffron
            : tint(t.brandColor, 0.9);
      const fg = b.tone === 'soft' ? ensureContrast(t.textColor, bg, 4.5) : readableOn(bg);
      // Soft callouts turn dark in dark mode (.gh-soft), so their text must follow (.gh-text).
      const textCls = b.tone === 'soft' ? ' class="gh-text"' : '';
      const title = b.title
        ? `<div${textCls} style="font-family:${head};font-size:28px;line-height:1.1;font-weight:800;letter-spacing:-0.02em;color:${fg};padding-bottom:${b.text ? '6px' : '0'};">${plainHtml(ctx, b.title)}</div>`
        : '';
      const text = b.text
        ? `<div${textCls} style="font-family:${body};font-size:15px;line-height:1.55;color:${fg};">${richHtml(ctx, b.text, fg)}</div>`
        : '';
      return row(
        `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td class="${b.tone === 'soft' ? 'gh-soft' : ''}" bgcolor="${bg}" style="background:${bg};border-radius:${t.radius}px;padding:22px 24px;text-align:${b.align};">${title}${text}</td></tr></table>`,
        '10px 40px',
      );
    }
    case 'social': {
      const items = b.items.filter((i) => i.url);
      if (!items.length) return '';
      const links = items
        .map(
          (i) =>
            `<a href="${href(ctx, i.url)}" target="_blank" style="display:inline-block;margin:0 4px 6px;padding:7px 14px;border:1px solid ${tint(t.textColor, 0.78)};border-radius:999px;font-family:${body};font-size:13px;line-height:1;color:${t.textColor};text-decoration:none;" class="gh-text gh-border">${escapeHtml(socialLabel(ctx, i.network))}</a>`,
        )
        .join('');
      return row(`<div style="text-align:${b.align};padding:8px 0;">${links}</div>`);
    }
    case 'footer': {
      if (!b.text.trim()) return '';
      const muted = ensureContrast(tint(t.textColor, 0.38), t.contentColor, 4.5);
      return row(
        `<p class="gh-muted" style="margin:0;padding:14px 0 6px;font-family:${body};font-size:13px;line-height:1.55;color:${muted};text-align:${b.align};">${richHtml(ctx, b.text, muted)}</p>`,
        '0 40px',
      );
    }
  }
}

function blockText(ctx: Ctx, b: EmailBlock | EmailCell): string {
  switch (b.type) {
    case 'heading':
      return plainText(ctx, b.text);
    case 'paragraph':
    case 'footer':
      return richText(ctx, b.text);
    case 'image': {
      const u = linkTarget(ctx, b.href);
      return u ? `${plainText(ctx, b.alt) || u}: ${u}` : '';
    }
    case 'button': {
      const u = linkTarget(ctx, b.url);
      return u ? `${plainText(ctx, b.label)}: ${u}` : plainText(ctx, b.label);
    }
    case 'callout':
      return [plainText(ctx, b.title), richText(ctx, b.text)].filter(Boolean).join('\n');
    case 'social':
      return b.items
        .filter((i) => i.url)
        .map((i) => `${socialLabel(ctx, i.network)}: ${i.url}`)
        .join('\n');
    case 'columns':
      return [...b.left, ...b.right]
        .map((c) => blockText(ctx, c))
        .filter(Boolean)
        .join('\n\n');
    case 'divider':
      return '* * *';
    default:
      return '';
  }
}

// ─── document ────────────────────────────────────────────────────────────────

/** Dark mode (clients that honour prefers-color-scheme): the app's deep navy night. */
const DARK = {
  bg: '#0B1622',
  card: '#122131',
  text: '#E8EEF4',
  muted: '#A5B3C2',
  border: '#22344A',
  soft: '#16304A',
};

function darkRules(prefix: string): string {
  return [
    `${prefix}.gh-bg{background:${DARK.bg}!important}`,
    `${prefix}.gh-card{background:${DARK.card}!important}`,
    `${prefix}.gh-text{color:${DARK.text}!important}`,
    `${prefix}.gh-muted{color:${DARK.muted}!important}`,
    `${prefix}.gh-border{border-color:${DARK.border}!important}`,
    `${prefix}.gh-soft{background:${DARK.soft}!important}`,
    `${prefix}.gh-brand{color:${DARK.text}!important}`,
  ].join('');
}

function headStyles(width: number): string {
  return `<style>
body{margin:0!important;padding:0!important;-webkit-text-size-adjust:100%;-ms-text-size-adjust:100%;}
table,td{border-collapse:collapse;mso-table-lspace:0;mso-table-rspace:0;}
img{-ms-interpolation-mode:bicubic;}
a[x-apple-data-detectors]{color:inherit!important;text-decoration:none!important;}
@media only screen and (max-width:${width + 20}px){
.gh-container{width:100%!important;max-width:100%!important;}
.gh-px{padding-left:22px!important;padding-right:22px!important;}
.gh-col{display:block!important;width:100%!important;padding-left:0!important;padding-right:0!important;}
.gh-btn-full{display:block!important;}
}
@media (prefers-color-scheme:dark){${darkRules('')}}
${darkRules('.gh-dark ')}
</style>`;
}

function preheaderHtml(text: string): string {
  if (!text) return '';
  // Padding characters keep clients from pulling body text into the preview line.
  const pad = '&#8199;&#65279;&#847; '.repeat(40);
  return `<div style="display:none;font-size:1px;line-height:1px;max-height:0;max-width:0;opacity:0;overflow:hidden;mso-hide:all;color:transparent;">${text}${pad}</div>`;
}

function renderBlocks(design: EmailBlocksDesign, opts: EmailRenderOptions): RenderedEmail {
  // Legibility is enforced, whatever the theme says (AI or hand-picked colours).
  const theme = {
    ...design.theme,
    textColor: ensureContrast(design.theme.textColor, design.theme.contentColor, 4.5),
  };
  const ctx: Ctx = {
    opts,
    theme,
    missing: new Set(),
    unknown: new Set(),
    dark: { text: DARK.text, muted: DARK.muted },
  };
  const width = theme.width;
  if (opts.textOnly) {
    const parts = design.blocks.map((b) => blockText(ctx, b)).filter(Boolean);
    let text = parts
      .join('\n\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
    text +=
      EMAIL_FOOTER_SEPARATOR + emailFooterText(opts.locale, opts.businessName, opts.unsubscribeUrl);
    return { html: '', text, missing: [...ctx.missing], unknown: [...ctx.unknown] };
  }
  const rows = design.blocks.map((b) => blockRow(ctx, b, width)).join('\n');
  // The unsubscribe footer sits on the page background: always ≥ 4.5:1 there.
  const muted = ensureContrast(tint(theme.textColor, 0.38), theme.backgroundColor, 4.5);
  const body = EMAIL_FONT_STACKS[theme.font];
  const pre = design.preheader ? plainHtml(ctx, design.preheader) : '';
  const footer = footerHtml(opts.locale, opts.businessName, opts.unsubscribeUrl, muted);
  const html = `<!DOCTYPE html>
<html lang="${opts.locale}" xmlns="http://www.w3.org/1999/xhtml"${opts.forceDark ? ' class="gh-dark"' : ''}>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<meta name="x-apple-disable-message-reformatting">
<meta name="color-scheme" content="light dark">
<meta name="supported-color-schemes" content="light dark">
<title>${escapeHtml(opts.subject)}</title>
${headStyles(width)}
</head>
<body class="gh-bg" style="margin:0;padding:0;background:${theme.backgroundColor};">
${preheaderHtml(pre)}
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" class="gh-bg" bgcolor="${theme.backgroundColor}" style="background:${theme.backgroundColor};">
<tr><td align="center" style="padding:28px 12px 20px;">
<table role="presentation" class="gh-container gh-card" width="${width}" cellpadding="0" cellspacing="0" border="0" bgcolor="${theme.contentColor}" style="width:${width}px;max-width:${width}px;background:${theme.contentColor};border-radius:${theme.radius}px;">
<tr><td style="height:12px;font-size:0;line-height:0;">&nbsp;</td></tr>
${rows}
<tr><td style="height:28px;font-size:0;line-height:0;">&nbsp;</td></tr>
</table>
<table role="presentation" class="gh-container" width="${width}" cellpadding="0" cellspacing="0" border="0" style="width:${width}px;max-width:${width}px;">
<tr><td class="gh-px gh-muted" style="padding:18px 40px 8px;font-family:${body};font-size:12px;line-height:1.55;color:${muted};text-align:center;">${footer}</td></tr>
</table>
</td></tr>
</table>
</body>
</html>`;
  const parts = design.blocks.map((b) => blockText(ctx, b)).filter(Boolean);
  let text = parts
    .join('\n\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  text +=
    EMAIL_FOOTER_SEPARATOR + emailFooterText(opts.locale, opts.businessName, opts.unsubscribeUrl);
  return { html, text, missing: [...ctx.missing], unknown: [...ctx.unknown] };
}

// ─── HTML mode (already sanitized) ───────────────────────────────────────────

const ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  '#39': "'",
};

export function decodeEntities(s: string): string {
  return s.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+\d*);/gi, (m, e: string) => {
    if (e[0] === '#') {
      const code =
        e[1] === 'x' || e[1] === 'X' ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10);
      return Number.isFinite(code) && code > 0 && code < 0x110000 ? String.fromCodePoint(code) : '';
    }
    return ENTITIES[e.toLowerCase()] ?? m;
  });
}

/** Plain-text alternative of (sanitized) HTML: block structure as line breaks, links as "label (url)". */
export function htmlToText(html: string): string {
  let s = html
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<head[\s\S]*?<\/head>/gi, '')
    .replace(/<(style|script|title)[\s\S]*?<\/\1>/gi, '')
    // Hidden preheaders are not part of the text.
    .replace(/<div[^>]*display:\s*none[^>]*>[\s\S]*?<\/div>/gi, '');
  s = s.replace(
    /<a\b[^>]*\bhref="([^"]*)"[^>]*>([\s\S]*?)<\/a>/gi,
    (_m, url: string, inner: string) => {
      const label = decodeEntities(inner.replace(/<[^>]+>/g, ''))
        .replace(/\s+/g, ' ')
        .trim();
      const u = decodeEntities(url);
      if (!/^(https?:|mailto:|tel:)/i.test(u)) return label;
      const shown = u.replace(/^mailto:/i, '');
      return !label || label === u || label === shown ? u : `${label} (${shown})`;
    },
  );
  s = s
    .replace(/<img\b[^>]*\balt="([^"]+)"[^>]*>/gi, (_m, alt: string) => ` ${alt} `)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(
      /<\/(p|div|h[1-6]|li|tr|table|blockquote|section|article|header|footer|ul|ol|center)>/gi,
      '\n\n',
    )
    .replace(/<li\b[^>]*>/gi, '• ')
    .replace(/<(td|th)\b[^>]*>/gi, ' ')
    .replace(/<hr\b[^>]*>/gi, '\n* * *\n')
    .replace(/<[^>]+>/g, '');
  s = decodeEntities(s)
    .replace(/[\u200b-\u200d\ufeff\u034f\u2007]/g, '')
    .replace(/[ \t ]+/g, ' ')
    .replace(/ *\n */g, '\n')
    .replace(/\n{3,}/g, '\n\n');
  return s.trim();
}

/**
 * Merge tokens allowed in HTML mode: a plain variable name and a fallback without markup or
 * quote characters, so a token can never span a tag or an attribute boundary.
 */
export const STRICT_MERGE_RE = /\{\{\s*([A-Za-z]+)\s*(?:\|([^{}<>"'&`]*))?\}\}/g;
const URL_ATTRS = new Set(['href', 'src', 'background']);

function strictValue(ctx: Ctx, name: string, fallback: string | undefined): string {
  const known = (MERGE_VARIABLES as readonly string[]).includes(name);
  if (!known) {
    ctx.unknown.add(name);
    return '';
  }
  const raw = ctx.opts.values[name as MergeVariable];
  const v = raw == null ? '' : String(raw).trim();
  if (v) return v;
  ctx.missing.add(name);
  return fallback?.trim() ?? '';
}

function mergeAttr(ctx: Ctx, name: string, value: string): string {
  if (!value.includes('{{')) return value;
  // CSS context: a merged value could inject declarations — never merged.
  if (name === 'style') return value.replace(STRICT_MERGE_RE, '');
  if (URL_ATTRS.has(name)) {
    if (/^\{\{\s*unsubscribeLink\s*\}\}$/.test(value.trim()))
      return escapeHtml(ctx.opts.unsubscribeUrl);
    // The personal form link (linked campaigns): a trusted server-built URL, whole attribute only.
    if (/^\{\{\s*formLink\s*\}\}$/.test(value.trim()))
      return escapeHtml(linkTarget(ctx, FORM_LINK_HREF));
    return value.replace(
      STRICT_MERGE_RE,
      (_all, n: string, fb: string | undefined, offset: number) => {
        // Only after the host (path/query/fragment), URL-encoded: never scheme or host.
        const before = decodeEntities(value.slice(0, offset));
        if (!/^https?:\/\/[^/?#\s]+[/?#]/i.test(before)) return '';
        return encodeURIComponent(strictValue(ctx, n, fb));
      },
    );
  }
  return value.replace(STRICT_MERGE_RE, (_all, n: string, fb: string | undefined) =>
    escapeHtml(strictValue(ctx, n, fb)),
  );
}

/**
 * Merge values into serialized (sanitized) markup, by context: text nodes get escaped values,
 * attributes escaped values (URL attributes: URL-encoded, after the host only; `style`: none),
 * `<style>`/`<script>` contents are never merged. Tokens that do not match the strict grammar are
 * left untouched (the sanitizer already neutralized them), so no value can create markup.
 */
function mergeHtmlStrict(ctx: Ctx, html: string): string {
  let out = '';
  let last = 0;
  let raw = false;
  const tagRe = /<!--[\s\S]*?-->|<[^>]*>/g;
  for (const m of html.matchAll(tagRe)) {
    const text = html.slice(last, m.index);
    out += raw
      ? text
      : text.replace(STRICT_MERGE_RE, (_a, n: string, fb: string | undefined) =>
          escapeHtml(strictValue(ctx, n, fb)),
        );
    const tag = m[0];
    if (/^<(style|script)\b/i.test(tag)) raw = true;
    else if (/^<\/(style|script)\b/i.test(tag)) raw = false;
    out +=
      tag.startsWith('</') || tag.startsWith('<!')
        ? tag
        : tag.replace(
            /(\s)([^\s"'=<>/]+)="([^"]*)"/g,
            (_a, sp: string, name: string, value: string) =>
              `${sp}${name}="${mergeAttr(ctx, name.toLowerCase(), value)}"`,
          );
    last = m.index + tag.length;
  }
  const rest = html.slice(last);
  out += raw
    ? rest
    : rest.replace(STRICT_MERGE_RE, (_a, n: string, fb: string | undefined) =>
        escapeHtml(strictValue(ctx, n, fb)),
      );
  return out;
}

/**
 * The unsubscribe footer of an HTML-mode email: its own white box with inline `!important`
 * styles (they beat any stylesheet rule, `!important` included) and a per-render class name no
 * stylesheet can target. Combined with the sanitizer (no position:absolute/fixed, no selectors on
 * `gh-` classes, `:last-*`, `[href]`, no hiding of html/body) it cannot be hidden or covered.
 */
function htmlModeFooter(opts: EmailRenderOptions): string {
  const id = `ghf${Math.random().toString(36).slice(2, 10)}`;
  const imp = (css: string) =>
    css
      .split(';')
      .filter(Boolean)
      .map((d) => `${d}!important`)
      .join(';');
  const box = imp(
    'display:table;visibility:visible;opacity:1;position:static;transform:none;width:100%;max-width:none;height:auto;margin:0;background:#FFFFFF;border:0;clip-path:none',
  );
  const cell = imp(
    `display:table-cell;visibility:visible;opacity:1;position:static;padding:20px 16px 24px;background:#FFFFFF;font-family:${EMAIL_FONT_STACKS.sans};font-size:12px;line-height:1.55;color:${EMAIL_PALETTE.inkMuted};text-align:center;text-indent:0;letter-spacing:normal;transform:none;clip-path:none`,
  );
  const link = imp(
    'display:inline;visibility:visible;opacity:1;position:static;font-size:12px;color:#24364A;text-decoration:underline;background:transparent;transform:none',
  );
  const lead =
    opts.locale === 'en'
      ? `You are receiving this email as a customer of ${escapeHtml(opts.businessName)}.`
      : `Λαμβάνετε αυτό το email ως πελάτης του ${escapeHtml(quoteName(opts.businessName))}.`;
  return `<table role="presentation" class="${id}" width="100%" cellpadding="0" cellspacing="0" border="0" style="${box}"><tr><td style="${cell}">${lead} <a href="${escapeHtml(opts.unsubscribeUrl)}" style="${link}">${opts.locale === 'en' ? 'Unsubscribe' : 'Διαγραφή από τη λίστα'}</a></td></tr></table>`;
}

function renderHtmlMode(
  design: Extract<EmailDesign, { mode: 'html' }>,
  opts: EmailRenderOptions,
): RenderedEmail {
  const ctx: Ctx = {
    opts,
    theme: {} as EmailTheme,
    missing: new Set(),
    unknown: new Set(),
    dark: { text: DARK.text, muted: DARK.muted },
  };
  let html = mergeHtmlStrict(ctx, design.html);
  if (opts.linkUrl) {
    const map = opts.linkUrl;
    html = html.replace(
      /(<a\b[^>]*?\bhref=")(https?:[^"]*)(")/gi,
      (_m, a: string, url: string, z: string) => {
        const real = decodeEntities(url);
        return `${a}${escapeHtml(map(real))}${z}`;
      },
    );
  }
  const text =
    htmlToText(html) +
    EMAIL_FOOTER_SEPARATOR +
    emailFooterText(opts.locale, opts.businessName, opts.unsubscribeUrl);
  if (opts.textOnly)
    return { html: '', text, missing: [...ctx.missing], unknown: [...ctx.unknown] };
  const footer = htmlModeFooter(opts);
  const pre = design.preheader ? preheaderHtml(plainHtml(ctx, design.preheader)) : '';
  if (/<body\b[^>]*>/i.test(html)) {
    html = html.replace(/<body\b[^>]*>/i, (m) => `${m}\n${pre}`);
    html = /<\/body>/i.test(html)
      ? html.replace(/<\/body>(?![\s\S]*<\/body>)/i, `${footer}\n</body>`)
      : `${html}\n${footer}`;
  } else {
    html = `<!DOCTYPE html>\n<html lang="${opts.locale}"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>${escapeHtml(opts.subject)}</title></head><body style="margin:0;padding:0;">\n${pre}${html}\n${footer}\n</body></html>`;
  }
  if (!/^\s*<!doctype/i.test(html)) html = `<!DOCTYPE html>\n${html}`;
  if (opts.forceDark) html = html.replace(/<html\b/i, '<html class="gh-dark"');
  return { html, text, missing: [...ctx.missing], unknown: [...ctx.unknown] };
}

/** Render a design for one recipient (or the preview). */
export function renderEmailDesign(design: EmailDesign, opts: EmailRenderOptions): RenderedEmail {
  return design.mode === 'html' ? renderHtmlMode(design, opts) : renderBlocks(design, opts);
}

/** Sample merge values for previews (Greek-first). */
export function sampleMergeValues(locale: 'el' | 'en', businessName: string): MergeValues {
  return locale === 'en'
    ? {
        firstName: 'Maria',
        lastName: 'Papadopoulou',
        displayName: 'Maria Papadopoulou',
        businessName,
        senderName: businessName,
      }
    : {
        firstName: 'Μαρία',
        lastName: 'Παπαδοπούλου',
        displayName: 'Μαρία Παπαδοπούλου',
        businessName,
        senderName: businessName,
      };
}
